import type { ClubHostingSchema, ClubSurfaceSchema, ClubTravelingSchema } from "@teamz/common";

import AddIcon from "@/assets/add.svg";
import EditIcon from "@/assets/edit.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clubHosting, clubHostingSchema, clubSurfaceSchema, clubTraveling, clubTravelingSchema, playerPreferredSurface } from "@teamz/common";
import { type ComponentProps, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useUserRole } from "@/hooks/use-user-role";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField } from "../ui/text-field";

type ClubSpecsType = "club-surface" | "club-hosting" | "club-traveling";

type SpecButtonProps = {
  data: string | null;
  spec: ClubSpecsType;
};

const SpecButton = ({ ref, data, spec, className, ...rest }: ComponentProps<"button"> & SpecButtonProps) => {
  const { t } = useTranslation(["profile"]);
  const userRole = useUserRole();
  const { user } = useMe();

  const labels: Record<ClubSpecsType, string> = {
    "club-surface": t("profile:surface"),
    "club-hosting": t("profile:hosting"),
    "club-traveling": t("profile:traveling"),
  };

  return (
    <button
      type="button"
      className={cn(
        "flex h-24 w-full flex-col rounded-xl border border-neutral-400 px-3 py-4 text-start",
        data ? "bg-white" : "bg-neutral-50",
        `group data-[color=blue]:bg-blue-500 data-[color=orange]:bg-orange-500 `,
        className,
        userRole === "player" && !user.isAdmin && "pointer-events-none",
      )}
      ref={ref}
      {...rest}
    >
      <div className="flex w-full items-center justify-between text-neutral-500 group-data-[color=blue]:text-neutral-450 group-data-[color=orange]:text-orange-300">
        <span className="w-4/5 text-sm font-normal leading-4 ">
          {labels[spec]}
        </span>
        {(userRole !== "player" || user.isAdmin) && (
          <div className="shrink-0">
            {data
              ? <EditIcon className="size-5" />
              : <AddIcon className="size-6 text-orange-500" />}
          </div>
        )}
      </div>
      {data && (
        <span className="mt-auto font-semibold leading-[18px] text-neutral-900 group-data-[color=blue]:text-white group-data-[color=orange]:text-white">
          {tDyn(t, `profile:${data}`)}
        </span>
      )}
    </button>
  );
};

function ClubSurfaceForm({ club, closeModal }: { club: RouterOutputs["club"]["getClub"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const clubSurfaceForm = useForm<ClubSurfaceSchema>({
    resolver: zodResolver(clubSurfaceSchema),
    defaultValues: {
      clubSurface: club.clubSurface || undefined,
    },
  });

  const onSubmit = clubSurfaceForm.handleSubmit((data) => {
    mutate({ ...data, clubRepresentativeId: club.clubRepresentativeId });
  });

  return (
    <>
      <SheetForm id="ClubSurfaceForm" onSubmit={onSubmit}>
        <Controller
          control={clubSurfaceForm.control}
          name="clubSurface"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:surface")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {playerPreferredSurface.map(surface => (
                      <SelectItem key={surface} value={surface}>{t(`profile:${surface}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="ClubSurfaceForm" loading={isPending || clubSurfaceForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function ClubHostingForm({ club, closeModal }: { club: RouterOutputs["club"]["getClub"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const clubHostingForm = useForm<ClubHostingSchema>({
    resolver: zodResolver(clubHostingSchema),
    defaultValues: {
      clubHosting: club.clubHosting || undefined,
    },
  });

  const onSubmit = clubHostingForm.handleSubmit((data) => {
    mutate({ ...data, clubRepresentativeId: club.clubRepresentativeId });
  });

  return (
    <>
      <SheetForm id="ClubHostingForm" onSubmit={onSubmit}>
        <Controller
          control={clubHostingForm.control}
          name="clubHosting"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:hosting")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {clubHosting.map(hosting => (
                      <SelectItem key={hosting} value={hosting}>{t(`profile:${hosting}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="ClubHostingForm" loading={isPending || clubHostingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

function ClubTravelingForm({ club, closeModal }: { club: RouterOutputs["club"]["getClub"]; closeModal: () => void }) {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.updateSpecs.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const clubTravelingForm = useForm<ClubTravelingSchema>({
    resolver: zodResolver(clubTravelingSchema),
    defaultValues: {
      clubTraveling: club.clubTraveling || undefined,
    },
  });

  const onSubmit = clubTravelingForm.handleSubmit((data) => {
    mutate({ ...data, clubRepresentativeId: club.clubRepresentativeId });
  });

  return (
    <>
      <SheetForm id="ClubTravelingForm" onSubmit={onSubmit}>
        <Controller
          control={clubTravelingForm.control}
          name="clubTraveling"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("profile:traveling")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {clubTraveling.map(traveling => (
                      <SelectItem key={traveling} value={traveling}>{t(`profile:${traveling}`)}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="ClubTravelingForm" loading={isPending || clubTravelingForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}

export function ClubSpecs({ club }: { club: RouterOutputs["club"]["getClub"] }) {
  const { t } = useTranslation(["profile"]);
  const userRole = useUserRole();
  const { user } = useMe();

  const labels: Record<ClubSpecsType, string> = {
    "club-surface": t("profile:surface"),
    "club-hosting": t("profile:hosting"),
    "club-traveling": t("profile:traveling"),
  };

  const [modalState, updateModalState] = useState<ClubSpecsType | "close">("close");

  const isModalOpen = modalState !== "close";
  function closeModal() {
    updateModalState("close");
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">
          {t("profile:specs")}
        </h3>

        <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
          <SpecButton
            data={club.clubSurface}
            spec="club-surface"
            data-color={club.clubSurface ? club.clubSurface.includes("hard") ? "blue" : "orange" : undefined}
            onClick={() => updateModalState("club-surface")}
          />
          <SpecButton data={club.clubHosting} spec="club-hosting" onClick={() => updateModalState("club-hosting")} />
          <SpecButton data={club.clubTraveling} spec="club-traveling" onClick={() => updateModalState("club-traveling")} />
        </div>
      </div>

      {(userRole !== "player" || user.isAdmin) && (
        <Sheet open={isModalOpen} onOpenChange={closeModal}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{isModalOpen && labels[modalState]}</SheetTitle>
            </SheetHeader>

            {modalState === "club-surface" && <ClubSurfaceForm club={club} closeModal={() => updateModalState("close")} />}
            {modalState === "club-hosting" && <ClubHostingForm club={club} closeModal={() => updateModalState("close")} />}
            {modalState === "club-traveling" && <ClubTravelingForm club={club} closeModal={() => updateModalState("close")} />}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
